import React from 'react';
import { Box, Checkbox, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/joy';
import {
  HandGrabbing as ClaimIcon,
  Ruler as MeasureIcon,
  ArrowCounterClockwise as ReleaseIcon,
  Prohibit as SkipIcon,
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import { formatQuoteRange, moneyToNumber } from '../../lib/directMailApi';
import {
  MAILER_PIN_STATUS_LABELS,
  MAIL_FULFILLMENT_LABELS,
  type MailerPin,
  type MailerPinStatus,
} from '../../types/directMail';

type ChipColor = 'primary' | 'neutral' | 'danger' | 'success' | 'warning';

const STATUS_COLORS: Record<MailerPinStatus, ChipColor> = {
  pinned: 'neutral',
  queued: 'primary',
  measuring: 'warning',
  measured: 'success',
  approved: 'success',
  batched: 'primary',
  mailed: 'primary',
  responded: 'success',
  skipped: 'danger',
};

function formatDay(value: string | null): string {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

export interface QueueTableProps {
  pins: MailerPin[];
  selectedIds: number[];
  onToggleSelected: (id: number) => void;
  /** Hidden entirely on tabs where nothing can be actioned in bulk. */
  selectable: boolean;
  currentUserId?: number;
  isOrgAdmin: boolean;
  busyPinId: number | null;
  /** The pin the page was opened on (?pin=), marked so it is findable in the rows. */
  highlightedPinId?: number | null;
  onClaim: (pin: MailerPin) => void;
  onRelease: (pin: MailerPin) => void;
  onMeasure: (pin: MailerPin) => void;
  onSkip: (pin: MailerPin) => void;
  emptyState: React.ReactNode;
}

export const QueueTable: React.FC<QueueTableProps> = ({
  pins,
  selectedIds,
  onToggleSelected,
  selectable,
  currentUserId,
  isOrgAdmin,
  busyPinId,
  highlightedPinId,
  onClaim,
  onRelease,
  onMeasure,
  onSkip,
  emptyState,
}) => {
  const selected = new Set(selectedIds);

  const columns: ResponsiveColumn<MailerPin>[] = [
    {
      key: 'address',
      sortValue: (row) => row.formatted_address,
      header: 'Address',
      width: '32%',
      mobileVariant: 'header',
      render: (pin) => (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {selectable && (
            <Checkbox
              size="sm"
              checked={selected.has(pin.id)}
              onChange={() => onToggleSelected(pin.id)}
              slotProps={{ input: { 'aria-label': `Select ${pin.formatted_address}` } }}
              sx={{ mt: 0.25 }}
            />
          )}
          <Box
            sx={{
              minWidth: 0,
              ...(pin.id === highlightedPinId && {
                borderLeft: '3px solid',
                borderColor: 'primary.solidBg',
                pl: 1,
              }),
            }}
          >
            <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
              {pin.formatted_address}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {[pin.city, pin.state, pin.postal_code].filter(Boolean).join(', ') || 'No city on file'}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: 'campaign',
      sortValue: (row) => row.campaign_name,
      header: 'Campaign',
      width: '16%',
      render: (pin) => (
        <Typography level="body-sm">{pin.campaign_name || 'Unassigned'}</Typography>
      ),
    },
    {
      key: 'pinned',
      sortValue: (row) => row.created_at ? new Date(row.created_at) : null,
      header: 'Pinned',
      width: '12%',
      render: (pin) => <Typography level="body-sm">{formatDay(pin.created_at)}</Typography>,
    },
    {
      key: 'claimed',
      sortValue: (row) => row.assigned_to_name,
      header: 'Claimed by',
      width: '14%',
      render: (pin) =>
        pin.assigned_to_name ? (
          <Typography level="body-sm">
            {pin.assigned_to === currentUserId ? 'You' : pin.assigned_to_name}
          </Typography>
        ) : (
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Unclaimed
          </Typography>
        ),
    },
    {
      key: 'status',
      sortValue: (row) => row.status,
      header: 'Status',
      width: '13%',
      render: (pin) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="sm" variant="soft" color={STATUS_COLORS[pin.status]}>
            {MAILER_PIN_STATUS_LABELS[pin.status]}
          </Chip>
          {pin.fulfillment === 'apexos' && (
            <Chip size="sm" variant="outlined" color="primary">
              {MAIL_FULFILLMENT_LABELS.apexos}
            </Chip>
          )}
        </Stack>
      ),
    },
    {
      key: 'quote',
      sortValue: (row) => Number(row.quote_price_low ?? 0),
      header: 'Quote',
      width: '13%',
      render: (pin) =>
        pin.is_measured ? (
          <Box>
            <Typography level="body-sm">
              {formatQuoteRange(pin.quote_price_low, pin.quote_price_high)}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {Math.round(moneyToNumber(pin.roof_area_sqft) / 100)} sq
              {pin.predominant_pitch ? ` · ${pin.predominant_pitch}` : ''}
            </Typography>
          </Box>
        ) : (
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Not measured
          </Typography>
        ),
    },
  ];

  return (
    <ResponsiveTable<MailerPin>
      rows={pins}
      getRowKey={(pin) => pin.id}
      columns={columns}
      actionsWidth="15%"
      emptyState={emptyState}
      rowActions={(pin) => {
        const busy = busyPinId === pin.id;
        const claimedByMe = pin.assigned_to != null && pin.assigned_to === currentUserId;
        const canRelease = pin.assigned_to != null && (claimedByMe || isOrgAdmin);
        const canClaim = pin.assigned_to == null && pin.status !== 'skipped';
        const canMeasure =
          claimedByMe || (isOrgAdmin && pin.assigned_to != null) || pin.status === 'measured';

        return (
          <Stack direction="row" spacing={0.5}>
            {canClaim && (
              <Tooltip title="Claim this pin">
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  loading={busy}
                  onClick={() => onClaim(pin)}
                >
                  <ClaimIcon />
                </IconButton>
              </Tooltip>
            )}
            {canMeasure && (
              <Tooltip title={pin.is_measured ? 'Re-measure' : 'Quick measure'}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="success"
                  disabled={busy}
                  onClick={() => onMeasure(pin)}
                >
                  <MeasureIcon />
                </IconButton>
              </Tooltip>
            )}
            {canRelease && (
              <Tooltip title="Release back to the queue">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  loading={busy}
                  onClick={() => onRelease(pin)}
                >
                  <ReleaseIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Skip this address">
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                disabled={busy}
                onClick={() => onSkip(pin)}
              >
                <SkipIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      }}
    />
  );
};
